import { Combobox, Text, useFilter } from "@ttc3k/trekker";
import {
  EnumOwnerType,
  Maybe,
  OperatorManyUseCaseEnum,
  SurveyQuestion,
  useOperatorsManyPersonConnectToListingQuery,
} from "gql/generated";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLoggedInAs } from "shared/hooks";
import { getMultilingualString } from "shared/utils";
import { Flex } from "styled-system/jsx";
import { useAppContext } from "useAppContext";

type WhereaboutsSearchInputProps = {
  question: SurveyQuestion;
  questionName: string;
  response: string[] | Maybe<string>[];
  onResponseChange: (questionId: string, response: string[]) => void;
  organizationId?: string;
};

export const WhereaboutsSearchInput = ({
  question,
  questionName,
  response,
  onResponseChange,
  organizationId,
}: WhereaboutsSearchInputProps) => {
  const { t } = useTranslation("booker", { keyPrefix: "QUESTIONNAIRE" });
  const loggedInAs = useLoggedInAs();
  const { operators: userOperators } = useAppContext();

  const orgId =
    loggedInAs?.type === EnumOwnerType.Organization
      ? loggedInAs.entityId
      : organizationId;

  const { data, loading } = useOperatorsManyPersonConnectToListingQuery({
    variables: {
      useCase: OperatorManyUseCaseEnum.Internal,
      includeInvitedOrAcceptedStaffCounts: false,
      filter: { organizations: [orgId] },
      limit: 10000,
    },
    skip: !orgId,
  });

  const operatorItems = useMemo(
    () =>
      data?.operatorMany?.map(op => ({
        value: op._entityId,
        label: getMultilingualString(op.name ?? {}),
      })) ?? [],
    [data],
  );

  const { contains } = useFilter({ sensitivity: "base" });
  const { collection, filter, set } = Combobox.useCollection({
    initialItems: operatorItems,
    filter: contains,
  });

  useEffect(() => {
    set(operatorItems);
  }, [operatorItems, set]);

  // Pre-select the form filler's first enrolled operator when no answer is set
  const [hasAutoSelected, setHasAutoSelected] = useState(false);
  useEffect(() => {
    if (hasAutoSelected || response[0]) return;
    if (!operatorItems.length || !userOperators?.length) return;

    const enrolledOperator = userOperators.find(op =>
      op.enrollments.some(
        e => e?.organization?._id === orgId,
      ),
    );
    if (enrolledOperator) {
      const match = operatorItems.find(
        item => item.value === enrolledOperator._entityId,
      );
      if (match) {
        onResponseChange(question._id, [match.value, match.label]);
        setHasAutoSelected(true);
      }
    }
  }, [
    hasAutoSelected,
    response,
    operatorItems,
    userOperators,
    orgId,
    onResponseChange,
    question._id,
  ]);

  const [inputValue, setInputValue] = useState("");

  const selectedLabel = useMemo(() => {
    if (response[1]) return response[1] as string;
    const match = operatorItems.find(op => op.value === response[0]);
    return match?.label ?? "";
  }, [response, operatorItems]);

  const handleValueChange = useCallback(
    (details: { value: string[] }) => {
      const selectedId = details.value[0] ?? "";
      if (selectedId) {
        const item = operatorItems.find(op => op.value === selectedId);
        onResponseChange(question._id, [
          selectedId,
          item?.label ?? "",
        ]);
      } else {
        onResponseChange(question._id, []);
      }
    },
    [onResponseChange, question._id, operatorItems],
  );

  return (
    <Flex flexDir="column" gap="50">
      <Text visual="bodyMedium" color="text.dark" whiteSpace="pre-wrap">
        {questionName}
      </Text>
      <Combobox.Root
        collection={collection}
        value={response[0] ? [response[0] as string] : []}
        onValueChange={handleValueChange}
        inputValue={inputValue}
        onInputValueChange={d => {
          setInputValue(d.inputValue);
          filter(d.inputValue);
        }}
        onOpenChange={d => {
          if (d.open) set(operatorItems);
        }}
        placeholder={selectedLabel || undefined}
        openOnClick
        allowCustomValue={false}
      >
        <Combobox.ItemGroup>
          {collection.items.length === 0 && !loading && (
            <Combobox.ItemGroupLabel>
              No listings found
            </Combobox.ItemGroupLabel>
          )}
          {loading && collection.items.length === 0 && (
            <Combobox.ItemGroupLabel>Loading...</Combobox.ItemGroupLabel>
          )}
          {collection.items.map(option => (
            <Combobox.Item
              key={option.value}
              label={option.label}
              item={option}
            />
          ))}
        </Combobox.ItemGroup>
      </Combobox.Root>
      {question.required && (
        <Text visual="smallRegular" color="text.mid">
          {t("REQUIRED")}
        </Text>
      )}
    </Flex>
  );
};
